import React, { useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { sendTransferEmails } from "../../lib/TransferEmail";
import moment from "moment";
import "../../styles/detail.css";

const UserPendingPayment = ({ user, userId, setUser }) => {
  const [editMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");

  const paymentData = user?.pendingPayment || {};

  const [updatedPayment, setUpdatedPayment] = useState({
    accountName: paymentData.accountName || "",
    accountType: paymentData.accountType || "",
    address: paymentData.address || "",
    amount: paymentData.amount || "",
    date: paymentData.date || "",
    memo: paymentData.memo || "",
    status: paymentData.status || "",
    type: paymentData.type || "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdatedPayment({ ...updatedPayment, [name]: value });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError("");
    setSuccessMessage("");
    try {
      const payload = {
        ...updatedPayment,
        amount: updatedPayment.amount ? Number(updatedPayment.amount) : "",
      };
      await updateDoc(doc(db, "users", userId), {
        pendingPayment: payload,
      });
      setUser({ ...user, pendingPayment: payload });
      setEditMode(false);
      setSuccessMessage("Pending payment updated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError(`Error saving: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm("Approve this payment? This will credit the user's account.")) return;
    setIsSaving(true);
    setError("");
    setSuccessMessage("");
    try {
      const transaction = {
        amount: Number(paymentData.amount) || 0,
        description: `${paymentData.accountName} - Payment approved`,
        date: moment().format('MMMM D, YYYY'),
        type: "credit",
        beneficiary: "Payment",
        account: paymentData.accountType || "Savings",
      };
      const existingTransactions = user.transactions || [];
      await updateDoc(doc(db, "users", userId), {
        transactions: [...existingTransactions, transaction],
        pendingPayment: null,
      });
      setUser({
        ...user,
        transactions: [...existingTransactions, transaction],
        pendingPayment: null,
      });
      sendTransferEmails(user.email, {
        sender: user.fullName || "User",
        senderAccount: paymentData.accountType || "Savings",
        beneficiary: paymentData.accountName || "N/A",
        bankName: "Sky High NTB",
        beneficiaryAccount: paymentData.address || "N/A",
        amount: Number(paymentData.amount) || 0,
        date: moment().format('MMMM D, YYYY'),
        type: "credit",
        status: "Completed",
        userId,
        transactionIndex: existingTransactions.length,
      });
      setSuccessMessage("Payment approved and credited successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError(`Error approving payment: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this payment?")) return;
    setIsDeleting(true);
    setError("");
    try {
      await updateDoc(doc(db, "users", userId), { pendingPayment: null });
      setUser({ ...user, pendingPayment: null });
      setEditMode(false);
      setSuccessMessage("Payment deleted");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError(`Error deleting: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="profile-grid detail-container">
      <div className="profile-field detail-full-width">
        <div className="profile-header-item detail-header">
          <div>
            <b className="profile-label">Pending Payment</b>
            <div className="profile-value detail-title-large">
              {paymentData.accountName || "No Active Payment"}
            </div>
          </div>
        </div>
      </div>

      <div className="profile-field">
        <b className="profile-label">Account Name</b>
        {editMode ? (
          <input type="text" className="profile-input" name="accountName" value={updatedPayment.accountName} onChange={handleChange} />
        ) : (
          <div className="profile-value">{paymentData.accountName || "N/A"}</div>
        )}
      </div>

      <div className="profile-field">
        <b className="profile-label">Account Type</b>
        {editMode ? (
          <input type="text" className="profile-input" name="accountType" value={updatedPayment.accountType} onChange={handleChange} />
        ) : (
          <div className="profile-value">{paymentData.accountType || "N/A"}</div>
        )}
      </div>

      <div className="profile-field">
        <b className="profile-label">Address</b>
        {editMode ? (
          <input type="text" className="profile-input" name="address" value={updatedPayment.address} onChange={handleChange} />
        ) : (
          <div className="profile-value">{paymentData.address || "N/A"}</div>
        )}
      </div>

      <div className="profile-field">
        <b className="profile-label">Amount</b>
        {editMode ? (
          <input type="number" className="profile-input" name="amount" value={updatedPayment.amount} onChange={handleChange} />
        ) : (
          <div className="profile-value">{paymentData.amount ?? "N/A"}</div>
        )}
      </div>

      <div className="profile-field">
        <b className="profile-label">Date</b>
        {editMode ? (
          <input
            type="datetime-local"
            className="profile-input"
            name="date"
            value={updatedPayment.date ? moment(updatedPayment.date).format('YYYY-MM-DDTHH:mm') : ''}
            onChange={handleChange}
          />
        ) : (
          <div className="profile-value">
            {paymentData?.date ? moment(paymentData.date).format('MMMM D, YYYY') : 'N/A'}
          </div>
        )}
      </div>

      <div className="profile-field">
        <b className="profile-label">Memo</b>
        {editMode ? (
          <textarea className="profile-input detail-textarea" name="memo" value={updatedPayment.memo} onChange={handleChange} rows={3} />
        ) : (
          <div className="profile-value">{paymentData.memo || "N/A"}</div>
        )}
      </div>

      <div className="profile-field">
        <b className="profile-label">Status</b>
        {editMode ? (
          <input type="text" className="profile-input" name="status" value={updatedPayment.status} onChange={handleChange} />
        ) : (
          <div className="profile-value">{paymentData.status || "N/A"}</div>
        )}
      </div>

      <div className="profile-field">
        <b className="profile-label">Type</b>
        {editMode ? (
          <input type="text" className="profile-input" name="type" value={updatedPayment.type} onChange={handleChange} />
        ) : (
          <div className="profile-value">{paymentData.type || "N/A"}</div>
        )}
      </div>

      <div className="detail-btn-group">
        {editMode ? (
          <>
            <button onClick={handleSave} className="profile-btn" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
            <button onClick={() => setEditMode(false)} className="detail-btn-cancel">
              Cancel
            </button>
          </>
        ) : (
          <button onClick={() => setEditMode(true)} className="profile-btn">
            Edit Payment Info
          </button>
        )}
        {!editMode && paymentData && paymentData.accountName && (
          <>
            <button
              onClick={handleApprove}
              disabled={isSaving}
              className="profile-btn"
              style={{ background: '#20B2AA', border: 'none' }}
            >
              {isSaving ? "Approving..." : "Approve"}
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="detail-btn-delete"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </button>
          </>
        )}
      </div>

      {successMessage && (
        <div className="detail-success">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="detail-error">{error}</div>
      )}
    </div>
  );
};

export default UserPendingPayment;